'use client';

import { ReactElement } from 'react';
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from 'remotion';

// Components
import { DotGridBackground } from '@/components/common/DotGridBackground';
import { TextSection } from './components/TextSection';

// Constants
import {
  CLAMP_BOTH,
  EMPLOYEE_YEAR_IN_REVIEW_CONFIG,
  SCENE_3A_BACKGROUND_FADE_OUT_DURATION,
} from '@/constants';

// Types
import { Project } from './types';

// Theme
import { employeeYearInReviewTheme } from '../../theme';

// Scene 3 to Scene 4 cross-fade timing
// Scene 4 starts 20 frames before Scene 3 ends
// This ensures Scene 4 is fully visible BEFORE Scene 3 starts fading out
const FADE_OUT_DURATION = SCENE_3A_BACKGROUND_FADE_OUT_DURATION;
const SCENE_DURATION = EMPLOYEE_YEAR_IN_REVIEW_CONFIG.timing.slide3a.duration;
const FADE_OUT_START = SCENE_DURATION - FADE_OUT_DURATION;

export interface Scene3aProps {
  projects: Project[];
  monthsOnProject?: number;
}

/**
 * Scene 3: Content slide with dot grid background
 *
 * This scene displays the dot grid background pattern
 * and will be extended with additional content.
 */
export const Scene3a = ({
  projects,
  monthsOnProject,
}: Scene3aProps): ReactElement => {
  const frame = useCurrentFrame();

  // Background fade out - smooth cross-fade with Scene 4
  const backgroundOpacity = interpolate(
    frame,
    [FADE_OUT_START, SCENE_DURATION],
    [1, 0],
    CLAMP_BOTH,
  );

  const { rawColors } = employeeYearInReviewTheme;

  return (
    <AbsoluteFill className={employeeYearInReviewTheme.colors.bgSecondary}>
      <Sequence name="DotGridBackground">
        <AbsoluteFill style={{ opacity: backgroundOpacity }}>
          <DotGridBackground lineColor={rawColors.ribbon.sheenWhite} />
        </AbsoluteFill>
      </Sequence>
      <Sequence name="TextSection">
        <TextSection projects={projects} monthsOnProject={monthsOnProject} />
      </Sequence>
    </AbsoluteFill>
  );
};
